import React, { useEffect, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { SectionShell, SmartLink, RichText } from '../shared';
import { GuideQuickLinks, quickLabel } from '../GuideQuickLinks';
import { fetchVisibleCollections } from '../../../lib/fabricCatalog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Live colorway count from the configurator catalog, optionally limited to collection names containing any of `match`.
const useFabricCount = (productType, match) => {
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (!productType) return;
    const terms = (match || '').split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
    fetchVisibleCollections(productType).then((data) => {
      let n = 0;
      (data || []).forEach((b) => (b.collections || []).forEach((c) => {
        if (terms.length && !terms.some((t) => (c.name || '').toLowerCase().includes(t))) return;
        n += (c.colors || []).length + (c.materials || []).reduce((s, m) => s + (m.colors || []).length, 0);
      }));
      setCount(n);
    }).catch(() => setCount(0));
  }, [productType, match]);
  return count;
};

const H2 = ({ children, id }) => children ? <h2 id={id} className="text-neutral-900">{children}</h2> : null;
const ctrlKey = (p) => ['a', 'b'].reverse().find((k) => /control/i.test(p[`options_${k}_title`] || '') && !/light control/i.test(p[`options_${k}_title`] || '') && (p[`options_${k}`] || []).length);

const LinkRow = ({ links = [], testId }) => links.length > 0 && (
  <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3" data-testid={testId}>
    {links.filter((l) => l.text).map((l, i) => (
      <SmartLink key={i} to={l.link} className="group inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-neutral-900 link-underline" data-testid={`${testId}-${i}`}>
        {l.text}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
      </SmartLink>
    ))}
  </div>
);

const Block = ({ children, testId }) => <div className="pt-16 mt-16 border-t border-neutral-200" data-testid={testId}>{children}</div>;

const Intro = ({ p }) => (
  <div className="grid lg:grid-cols-[5fr_7fr] gap-10 lg:gap-20" data-testid="pc-intro">
    <div>
      {p.eyebrow && <span className="label-overline text-[color:var(--site-accent)]">{p.eyebrow}</span>}
      <div className="mt-4"><H2>{p.title}</H2></div>
    </div>
    <RichText html={p.body} className="text-lg text-neutral-600 leading-relaxed" />
  </div>
);

const Benefits = ({ p }) => (p.benefits || []).length > 0 && (
  <Block testId="pc-benefits">
    <H2>{p.benefits_title}</H2>
    <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
      {p.benefits.map((b, i) => (
        <li key={i} className="flex gap-5">
          <span className="text-sm font-medium text-[color:var(--site-accent)] tabular-nums pt-1">{String(i + 1).padStart(2, '0')}</span>
          <div><h3 className="text-lg font-medium text-neutral-900">{b.title}</h3><p className="mt-2 text-neutral-600">{b.text}</p></div>
        </li>
      ))}
    </ol>
  </Block>
);

const OptionList = ({ title, items = [], testId, id }) => items.length > 0 && (
  <div data-testid={testId}>
    {title && <h3 id={id} className="text-xl font-medium text-neutral-900">{title}</h3>}
    <dl className="mt-6 divide-y divide-neutral-200 border-y border-neutral-200">
      {items.map((o, i) => (
        <div key={i} className="grid grid-cols-[7rem_1fr] sm:grid-cols-[9rem_1fr] gap-4 py-4">
          <dt className="font-medium text-neutral-900">{o.name}</dt><dd className="text-neutral-600">{o.text}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const hasOptions = (p) => (p.options_a || []).length > 0 || (p.options_b || []).length > 0;
const Options = ({ p, sid }) => hasOptions(p) && (
  <Block testId="pc-options">
    <H2 id={`${sid}-options`}>{p.options_title}</H2>
    <div className="mt-10 grid lg:grid-cols-2 gap-12">
      <OptionList title={p.options_a_title} items={p.options_a} testId="pc-options-a" id={ctrlKey(p) === 'a' ? `${sid}-controls` : undefined} />
      <OptionList title={p.options_b_title} items={p.options_b} testId="pc-options-b" id={ctrlKey(p) === 'b' ? `${sid}-controls` : undefined} />
    </div>
  </Block>
);

const Fabrics = ({ p, sid }) => {
  const count = useFabricCount(p.fabric_product_type, p.fabric_collections_match);
  const text = count ? (p.fabrics_body || '').replace(/\{fabric_count\}/g, count) : (p.fabrics_fallback || (p.fabrics_body || '').replace(/\{fabric_count\}\s*/g, ''));
  if (!p.fabrics_title && !text) return null;
  return (
    <Block testId="pc-fabrics">
      <div className="grid lg:grid-cols-[5fr_7fr] gap-10 lg:gap-20">
        <H2 id={`${sid}-fabrics`}>{p.fabrics_title}</H2>
        <div>
          <p className="text-lg text-neutral-600 leading-relaxed" data-testid="pc-fabrics-text">{text}</p>
          {(p.fabric_collections || []).length > 0 && (
            <ul className="mt-8 grid sm:grid-cols-2 gap-4">
              {p.fabric_collections.map((c, i) => (
                <li key={i} className="p-6 bg-[color:var(--site-surface)]"><h3 className="font-medium text-neutral-900">{c.name}</h3><p className="mt-2 text-sm text-neutral-600">{c.text}</p></li>
              ))}
            </ul>
          )}
          <LinkRow links={p.fabrics_links} testId="pc-fabrics-links" />
        </div>
      </div>
    </Block>
  );
};

const Uses = ({ p, sid }) => (p.uses || []).length > 0 && (
  <Block testId="pc-uses">
    <H2 id={`${sid}-uses`}>{p.uses_title}</H2>
    <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {p.uses.map((u, i) => (
        <li key={i} className="border-l-2 border-[color:var(--site-accent)] pl-5"><h3 className="font-medium text-neutral-900">{u.title}</h3><p className="mt-1 text-neutral-600">{u.text}</p></li>
      ))}
    </ul>
  </Block>
);

const CareMeasure = ({ p, sid }) => ((p.care || []).length > 0 || p.measure_body) && (
  <Block testId="pc-care-measure">
    <div className="grid lg:grid-cols-2 gap-16">
      {(p.care || []).length > 0 && (
        <div data-testid="pc-care">
          <H2 id={`${sid}-care`}>{p.care_title}</H2>
          <ul className="mt-8 space-y-4">
            {p.care.map((c, i) => <li key={i} className="flex gap-3 text-neutral-600"><Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-[color:var(--site-accent)]" />{c.text}</li>)}
          </ul>
        </div>
      )}
      {p.measure_body && (
        <div data-testid="pc-measure">
          <H2 id={`${sid}-measure`}>{p.measure_title}</H2>
          <p className="mt-8 text-neutral-600 leading-relaxed">{p.measure_body}</p>
          <LinkRow links={p.measure_links} testId="pc-measure-links" />
        </div>
      )}
    </div>
  </Block>
);

const Related = ({ p }) => (p.related || []).length > 0 && (
  <Block testId="pc-related">
    <nav aria-label={p.related_title || 'Related'}>
      <H2>{p.related_title}</H2>
      <div className="mt-8 flex flex-wrap gap-3">
        {p.related.filter((l) => l.text).map((l, i) => (
          <SmartLink key={i} to={l.link} className="px-5 py-2.5 border border-neutral-300 text-sm text-neutral-900 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 transition-colors" data-testid={`pc-related-link-${i}`}>{l.text}</SmartLink>
        ))}
      </div>
    </nav>
  </Block>
);

// Jump links for blocks that exist in this guide plus the deep-dive topics and FAQ that follow it.
const guideLinks = (section, sections = []) => {
  const p = section.props || {}, sid = section.style?.anchor || section.id;
  const own = [
    ['options', hasOptions(p) && p.options_title, quickLabel('options', p.options_title)],
    ['controls', ctrlKey(p), 'Controls'],
    ['fabrics', p.fabrics_title, quickLabel('fabrics', p.fabrics_title)],
    ['uses', (p.uses || []).length && p.uses_title, quickLabel('uses', p.uses_title)],
    ['care', (p.care || []).length && p.care_title, quickLabel('care', p.care_title)],
    ['measure', p.measure_body && p.measure_title, quickLabel('measure', p.measure_title)],
  ].filter(([, ok]) => ok).map(([key, , label]) => ({ key, id: `${sid}-${key}`, label }));
  const after = sections.slice(sections.findIndex((s) => s.id === section.id) + 1);
  const extra = after.filter((s) => ['product_topic', 'faq'].includes(s.type)).map((s, i) => ({
    key: s.type === 'faq' ? 'faq' : `topic-${i}`, id: s.style?.anchor || s.id,
    label: s.type === 'faq' ? 'FAQ' : (s.props?.nav_label || quickLabel('topic', s.props?.title)),
  })).filter((l, i, arr) => l.label && arr.findIndex((x) => x.label === l.label) === i);
  return [...own, ...extra];
};

export const ProductContentSection = ({ section, sections }) => {
  const p = section.props || {}, sid = section.style?.anchor || section.id;
  return (
    <SectionShell section={section} testId="section-product-content">
      <Intro p={p} />
      <GuideQuickLinks links={guideLinks(section, sections)} />
      <Benefits p={p} />
      <Options p={p} sid={sid} />
      <Fabrics p={p} sid={sid} />
      <Uses p={p} sid={sid} />
      <CareMeasure p={p} sid={sid} />
      <Related p={p} />
    </SectionShell>
  );
};

// Reusable deep-dive block for a guide (e.g. oversized shades): intro, explainer + notes, considerations, callout, links.
export const ProductTopicSection = ({ section }) => {
  const p = section.props || {};
  return (
    <SectionShell section={section} testId="section-product-topic">
      <Intro p={p} />
      {(p.a_body || (p.a_points || []).length > 0) && (
        <Block testId="pt-explainer">
          <div className="grid lg:grid-cols-[5fr_7fr] gap-10 lg:gap-20">
            <H2>{p.a_title}</H2>
            <div>
              {p.a_body && <p className="text-lg text-neutral-600 leading-relaxed" data-testid="pt-explainer-text">{p.a_body}</p>}
              {(p.a_points || []).length > 0 && (
                <ul className="mt-6 space-y-3">
                  {p.a_points.map((x, i) => <li key={i} className="flex gap-3 text-neutral-600"><Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-[color:var(--site-accent)]" />{x.text}</li>)}
                </ul>
              )}
            </div>
          </div>
        </Block>
      )}
      {(p.b_items || []).length > 0 && (
        <Block testId="pt-considerations">
          <H2>{p.b_title}</H2>
          <dl className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
            {p.b_items.map((b, i) => (
              <div key={i} className="border-l-2 border-[color:var(--site-accent)] pl-5">
                <dt className="font-medium text-neutral-900">{b.title}</dt><dd className="mt-1 text-neutral-600">{b.text}</dd>
              </div>
            ))}
          </dl>
        </Block>
      )}
      {p.note_body && (
        <Block testId="pt-note">
          <div className="p-6 md:p-10 bg-[color:var(--site-surface)]">
            <H2>{p.note_title}</H2>
            <RichText html={p.note_body} className="mt-4 text-neutral-700 leading-relaxed" />
            <LinkRow links={p.links} testId="pt-links" />
          </div>
        </Block>
      )}
    </SectionShell>
  );
};

// Small 4:3 thumbnail; pre-sized /img/opt/*-480.webp files also get a 240w source.
const RoomThumb = ({ image, room, testId }) => {
  const small = image.url.endsWith('-480.webp') ? image.url.replace('-480.webp', '-240.webp') : null;
  return (
    <img src={image.url} srcSet={small ? `${small} 240w, ${image.url} 480w` : undefined} sizes="(min-width: 640px) 112px, 88px"
      alt={image.alt_text || room} width={480} height={360} loading="lazy" decoding="async"
      className="w-[88px] h-[66px] sm:w-28 sm:h-[84px] flex-shrink-0 object-cover bg-neutral-100" data-testid={testId} />
  );
};

// Room-by-room recommendations: each room lists suggested product combinations plus the considerations behind them.
export const RoomGuideSection = ({ section }) => {
  const p = section.props || {};
  return (
    <SectionShell section={section} testId="section-room-guide">
      <Intro p={p} />
      {(p.rooms || []).length > 0 && (
        <div className="mt-16 grid md:grid-cols-2 gap-x-12 gap-y-0 border-t border-neutral-200" data-testid="room-guide-list">
          {p.rooms.map((r, i) => {
            const picks = (r.picks || '').split(',').map((x) => x.trim()).filter(Boolean);
            return (
              <article key={i} className="py-10 border-b border-neutral-200" data-testid={`room-guide-item-${i}`}>
                <div className="flex items-center gap-4">
                  {r.image?.url && <RoomThumb image={r.image} room={r.room} testId={`room-guide-thumb-${i}`} />}
                  <h3 className="text-xl font-medium text-neutral-900">{r.room}</h3>
                </div>
                {picks.length > 0 && (
                  <ul className="mt-4 flex flex-wrap gap-2" aria-label={`Suggested for ${r.room}`}>
                    {picks.map((x) => <li key={x} className="px-3 py-1 text-xs border border-neutral-300 text-neutral-700 bg-white">{x}</li>)}
                  </ul>
                )}
                {r.text && <p className="mt-4 text-neutral-600 leading-relaxed">{r.text}</p>}
                {r.consider && <p className="mt-3 text-sm text-neutral-500"><span className="font-medium text-neutral-700">Consider: </span>{r.consider}</p>}
              </article>
            );
          })}
        </div>
      )}
      {(p.note_body || (p.links || []).length > 0) && (
        <Block testId="room-guide-note">
          <H2>{p.note_title}</H2>
          {p.note_body && <RichText html={p.note_body} className="mt-4 max-w-3xl text-neutral-600 leading-relaxed" />}
          <LinkRow links={p.links} testId="room-guide-links" />
        </Block>
      )}
    </SectionShell>
  );
};
